/* eslint-disable react/jsx-no-duplicate-props */
import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import { Fab, TextField } from '@mui/material';
import toast from 'react-hot-toast';
import CloseIcon from '@mui/icons-material/Close';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import _ from 'lodash';
import * as Yup from 'yup';
import { Select } from 'antd';

import { createSafalEventTodo, updateSafalEventTodo } from '../../../services/Service';
import DateField from '../../../components/UI/DateField';
import { transformYupErrorsIntoArray } from '../../../utils/fn';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 900,
  bgcolor: 'background.paper',
  border: '1px solid #000',
  borderRadius: '8px',
  boxShadow: 24,
  displey: 'flex',
  maxHeight: '95vh',
  overflow: 'auto',
  flexDirection: 'column',
  gap: '12px',
  p: 4,
  '@media (max-width: 640px)': {
    width: '95vw',
    height: '95vh',
    overflow: 'auto',
  },
};

const validationSchema = Yup.object().shape({
  description: Yup.string().required('required'),
});

const resetFieldValue = {
  planningPhase: '', // optional
  description: '', // required
  timeline: '', // optional
  notes: '', // optional
  targetDate: '', // optional ISO feature date
  assignee: [], // required assignee array
  status: '', // optional
};

export default function AddModal({ data, open, onClose, isDisplay, eventType, eventInfo, peopleAccepted }) {
  const { user } = useSelector((state) => state.login);
  // const dispatch = useDispatch();

  const [fieldValue, setFieldValue] = React.useState({ ...resetFieldValue });
  const [fieldValueError, setFieldValueError] = React.useState([]);
  const [isDisableAll, setIsDisableAll] = React.useState(false);

  const [imagesFile, setImagesFile] = React.useState(null);

  const eventTypeList = eventType || [];

  const onCloseThis = (refetchFlag) => {
    setIsDisableAll(false);
    setFieldValue({ ...resetFieldValue });
    setFieldValueError([]);
    setImagesFile(null);
    onClose(refetchFlag);
  };

  const updateFieldValue = data
    ? {
        planningPhase: data?.date ?? '',
        status: data?.status ?? '',
        description: data?.description ?? '',
        timeline: data?.timeline ?? '',
        notes: data?.notes ?? '',
        targetDate: data?.targetDate ?? '',
        assignee: data?.assignee?.map((e) => e?.user) ?? [],
      }
    : resetFieldValue;

  const handleClose = async (refetchFlag, directClose) => {
    if (isDisplay) {
      onCloseThis(refetchFlag);
      return;
    }
    if (directClose) {
      onCloseThis(refetchFlag);
      return;
    }

    if (_.isEqual(updateFieldValue, fieldValue)) {
      onCloseThis(refetchFlag);
      return;
    }
    const result = await Swal.fire({
      text: 'Are you sure you want to close this?',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes',
      appendTo: 'body',
      customClass: {
        container: 'my-swal', // Add a custom class for styling
      },
      onBeforeOpen: () => {
        // Set a higher zIndex for the Swal modal
        document.querySelector('.my-swal').style.zIndex = 999999;
      },
    });

    if (result.isConfirmed) {
      onCloseThis(refetchFlag);
    }
  };

  React.useEffect(() => {
    if (open === true && data !== null) {
      setFieldValue(updateFieldValue);
      setIsDisableAll(isDisplay);
    }
  }, [open, data, isDisplay]);

  const onSubmit = async () => {
    setIsDisableAll(true);
    setFieldValueError([]);
    try {
      const payload = {
        ...fieldValue,
        assignee: fieldValue?.assignee?.map((id) => ({
          user: id,
        })),
        event: eventInfo._id,
      };

      await validationSchema.validate(payload, { abortEarly: false, strict: false });

      const res = await createSafalEventTodo(payload);
      if (res.status === 200) {
        toast.success(res.data.message);
        handleClose(true, true);
      }
    } catch (err) {
      setFieldValueError(transformYupErrorsIntoArray(err));
      toast.error(err?.response?.data?.message || err.message);
    } finally {
      setIsDisableAll(false);
    }
  };

  const onUpdate = async () => {
    setIsDisableAll(true);
    setFieldValueError([]);
    try {
      const payload = {
        ...fieldValue,
        assignee: fieldValue?.assignee?.map((id) => ({
          user: id,
        })),
      };
      await validationSchema.validate(payload, { abortEarly: false, strict: false });

      const res = await updateSafalEventTodo({ ...payload, _id: data._id });
      if (res.status === 200) {
        toast.success(res.data.message);
        handleClose(true, true);
      }
    } catch (err) {
      setFieldValueError(transformYupErrorsIntoArray(err));
      toast.error(err?.response?.data?.message || err.message);
    } finally {
      setIsDisableAll(false);
    }
  };

  // const amountSym = user.country === 'United States' ? '$' : '₹';

  const assigneeList = peopleAccepted?.map((exs) => ({
    label: `${exs?.firstName} ${exs?.lastName} (${exs?.email})`,
    value: exs?._id,
  }));

  if (!assigneeList?.find((epp) => epp?.value === user?._id)) {
    assigneeList.push({
      label: `My Self`,
      value: user?._id,
    });
  }
  if (!assigneeList?.find((epp) => epp?.value === eventInfo?.user)) {
    assigneeList.push({
      label: `Event Owner`,
      value: eventInfo?.user,
    });
  }

  return (
    <>
      <Modal sx={{ zIndex: 345 }} open={open} onClose={handleClose}>
        <Box sx={style}>
          <Fab
            sx={{
              position: 'absolute',
              top: '10px',
              right: '10px',
            }}
            onClick={handleClose}
            size="small"
            color="primary"
            aria-label="add"
          >
            <CloseIcon />
          </Fab>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="h4" gutterBottom sx={{ fontSize: '24px', fontWeight: 700, color: '#3D71FF' }}>
              {data === null
                ? `Add Event Task (${eventInfo?.name})`
                : isDisplay
                ? `Task (${eventInfo?.name})`
                : `Update Task (${eventInfo?.name})`}
            </Typography>
            {/* {isDisplayModeActive && (
              <TopTitleAction
                data={editData}
                onDelete={onDeleteModalInit}
                isEdit={isDisplayModeActive}
                onEdit={() => setIsDisplayModeActive(false)}
                isChangeLogVisible={onChangeLog !== undefined}
                onChangeLog={() => onChangeLog(editData)}
              />
            )} */}
          </div>
          <div className="sm-flex-direction-column" style={{ display: 'flex', gap: '16px', marginTop: '16px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', flex: '1 1 0%' }}>
              {/* description */}
              <TextField
                disabled={isDisableAll}
                value={fieldValue.description}
                onChange={(e) => setFieldValue({ ...fieldValue, description: e.target.value })}
                label={
                  <Typography sx={{ color: '#B6B6B6' }}>
                    Task Description <span style={{ color: 'red' }}>*</span>
                  </Typography>
                }
                size="small"
                type="text"
                fullWidth
                variant="standard"
                InputLabelProps={{
                  style: { color: 'black' },
                  // shrink: true,
                }}
                error={fieldValueError?.find((e) => e.key === 'description')}
                helperText={fieldValueError?.find((e) => e.key === 'description')?.error}
                inputProps={{
                  maxlength: 250,
                }}
              />
              {/* planningPhase */}
              <TextField
                disabled={isDisableAll}
                value={fieldValue.planningPhase}
                onChange={(e) => setFieldValue({ ...fieldValue, planningPhase: e.target.value })}
                label={<Typography sx={{ color: '#B6B6B6' }}>Planning Phase</Typography>}
                size="small"
                type="text"
                fullWidth
                variant="standard"
                InputLabelProps={{
                  style: { color: 'black' },
                  // shrink: true,
                }}
                inputProps={{
                  maxlength: 500,
                }}
                error={fieldValueError?.find((e) => e.key === 'planningPhase')}
                helperText={fieldValueError?.find((e) => e.key === 'planningPhase')?.error}
              />
              {/* targetDate */}
              <DateField
                disablePast
                disabled={isDisableAll}
                value={fieldValue.targetDate}
                fullWidth
                variant="standard"
                label={<Typography sx={{ color: '#B6B6B6' }}>Target Date</Typography>}
                onChange={(e) => e?.isValid() && setFieldValue((prev) => ({ ...prev, targetDate: e.toISOString() }))}
                error={fieldValueError?.find((e) => e.key === 'targetDate')}
                helperText={fieldValueError?.find((e) => e.key === 'targetDate')?.error}
              />
              {/* timeline */}
              <TextField
                disabled={isDisableAll}
                value={fieldValue.timeline}
                onChange={(e) => setFieldValue({ ...fieldValue, timeline: e.target.value })}
                label={<Typography sx={{ color: '#B6B6B6' }}>Estimate Timeline</Typography>}
                size="small"
                type="text"
                fullWidth
                variant="standard"
                InputLabelProps={{
                  style: { color: 'black' },
                  // shrink: true,
                }}
                error={fieldValueError?.find((e) => e.key === 'timeline')}
                helperText={fieldValueError?.find((e) => e.key === 'timeline')?.error}
                inputProps={{
                  maxlength: 250,
                }}
              />
              {/* notes */}
              <TextField
                disabled={isDisableAll}
                value={fieldValue.notes}
                onChange={(e) => setFieldValue({ ...fieldValue, notes: e.target.value })}
                label={<Typography sx={{ color: '#B6B6B6' }}>Notes</Typography>}
                size="small"
                type="text"
                fullWidth
                variant="standard"
                InputLabelProps={{
                  style: { color: 'black' },
                  // shrink: true,
                }}
                error={fieldValueError?.find((e) => e.key === 'notes')}
                helperText={fieldValueError?.find((e) => e.key === 'notes')?.error}
                inputProps={{
                  maxlength: 250,
                }}
              />

              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span>
                  <Typography sx={{ color: '#B6B6B6' }}>Assignee</Typography>
                </span>
                <Select
                  title="Assignee"
                  mode="multiple"
                  allowClear
                  disabled={isDisableAll}
                  style={{
                    flex: '1',
                  }}
                  variant="outlined"
                  dropdownStyle={{
                    zIndex: 20000,
                  }}
                  value={fieldValue.assignee}
                  // defaultValue={fieldValue.state}
                  placeholder="Select people"
                  onChange={(values) => setFieldValue((prev) => ({ ...prev, assignee: values }))}
                  options={assigneeList}
                />
              </div>
              {/* status */}
              {/* <TextField
                disabled={isDisableAll}
                value={fieldValue.status}
                onChange={(e) => setFieldValue({ ...fieldValue, status: e.target.value })}
                label={<Typography sx={{ color: '#B6B6B6' }}>Status</Typography>}
                size="small"
                type="text"
                fullWidth
                variant="standard"
                InputLabelProps={{
                  style: { color: 'black' },
                  // shrink: true,
                }}
                inputProps={{
                  maxlength: 250,
                }}
                select
              >
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Inactive">Inactive</MenuItem>
              </TextField> */}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px', marginTop: '22px', justifyContent: 'end' }}>
            <Button
              disabled={isDisplay ? false : isDisableAll}
              sx={{
                backgroundColor: '#ff4842',
                ':hover': { backgroundColor: '#d40700' },
                color: 'white',
                borderRadius: '30px',
                textTransform: 'capitalize',
              }}
              onClick={handleClose}
              variant="contained"
            >
              Cancel
            </Button>
            {!isDisplay && data === null && (
              <Button
                disabled={isDisableAll}
                onClick={onSubmit}
                sx={{ backgroundColor: '#3D71FF', borderRadius: '30px', textTransform: 'capitalize' }}
                variant="contained"
              >
                Add
              </Button>
            )}
            {!isDisplay && data !== null && (
              <Button
                disabled={isDisableAll}
                onClick={onUpdate}
                sx={{ backgroundColor: '#3D71FF', borderRadius: '30px', textTransform: 'capitalize' }}
                variant="contained"
              >
                Save
              </Button>
            )}
          </div>
        </Box>
      </Modal>
    </>
  );
}
